import React, { useEffect, useState, useRef } from 'react';
import * as Tiff from 'tiff.js';

const TifViewer = ({ tifUrl, scaleRatio = 0.1, compressToJpeg = false, jpegQuality = 1 }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const offscreenCanvasRef = useRef(document.createElement('canvas')); // 用于离屏绘制和缩放

  useEffect(() => {
    const loadTiff = async () => {
      try {
        setLoading(true);
        setError(null);

        // 初始化tiff.js内存，处理大文件[6,7,8](@ref)
        Tiff.initialize({ TOTAL_MEMORY: 256 * 1024 * 1024 }); // 设置为256MB

        const response = await fetch(tifUrl);
        const arrayBuffer = await response.arrayBuffer();
        
        const tiff = new Tiff({ buffer: arrayBuffer });
        const originalCanvas = tiff.toCanvas();

        // --- 核心缩放逻辑 ---
        const offscreenCtx = offscreenCanvasRef.current.getContext('2d');
        
        // 1. 计算缩放后的尺寸
        const newWidth = originalCanvas.width * scaleRatio;
        const newHeight = originalCanvas.height * scaleRatio;
        
        // 2. 设置离屏canvas尺寸
        offscreenCanvasRef.current.width = newWidth;
        offscreenCanvasRef.current.height = newHeight;
        
        // 3. 使用图像平滑算法在离屏canvas上绘制缩放后的图像
        offscreenCtx.imageSmoothingQuality = 'high';
        offscreenCtx.drawImage(originalCanvas, 0, 0, originalCanvas.width, originalCanvas.height, 0, 0, newWidth, newHeight);
        
        // --- 可选：压缩为JPEG格式以减小数据大小[2,3](@ref) ---
        let finalDataUrl;
        if (compressToJpeg) {
          // 转换为JPEG格式，并指定质量（0-1之间）
          finalDataUrl = offscreenCanvasRef.current.toDataURL('image/jpeg', jpegQuality);
        } else {
          // 保持PNG格式（无损，但文件较大）
          finalDataUrl = offscreenCanvasRef.current.toDataURL();
        }

        // 创建新的Image对象，其src为处理后的DataURL
        const img = new Image();
        img.onload = () => {
          setImageData(img); // 将加载完成的图像对象存入状态
        };
        img.src = finalDataUrl;

      } catch (err) {
        console.error('TIF文件处理失败:', err);
        setError('无法加载TIF文件: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTiff();
  }, [tifUrl, scaleRatio, compressToJpeg, jpegQuality]); // 依赖项中包含缩放和压缩参数

  // 将处理后的图像绘制到显示用的canvas上
  useEffect(() => {
    if (imageData && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      canvasRef.current.width = imageData.width;
      canvasRef.current.height = imageData.height;
      ctx.drawImage(imageData, 0, 0);
    }
  }, [imageData]);

  if (loading) return <div className="loading">正在加载并处理TIF图片...</div>;
  if (error) return <div className="error">{error}</div>;

  return <canvas ref={canvasRef} className="tif-canvas" />;
};

export default TifViewer;